/**
 * Shading (shadow) simulation from the sun position: which parts of each PV module are in the shadow of buildings,
 * roofs, parapets, obstacles, structures, equipment and other modules, at a time, over a day or over a year.
 *
 * Geometry: triangles in the design's 3D world (metres; X → right of the drawing, Y up, Z → down the drawing; the top
 * of the drawing points to `upBearingDeg`). For each module and sun direction, every triangle in front of the module
 * plane (towards the sun) whose shadow can reach the module is clipped to that side of the plane and projected onto the
 * module along the sun direction; the shaded area is the union of these shadow polygons over the module, measured on
 * parallel strips (exact along each strip, midpoint rule across) — thin shadow bands such as row-to-row shading on the
 * lower edge are measured, not missed between sample points. Where shadows overlap, the area is attributed to the
 * nearest occluder (category losses). Candidate triangles come from a uniform grid built in the plane perpendicular to
 * the sun direction (each triangle binned by its projected bounding box).
 *
 * Energy figures are CLEAR-SKY DIRECT-BEAM figures (sun position NOAA, Haurwitz GHI, Erbs split — solar.ts): the share
 * of the beam irradiance on each module lost to shadows. Diffuse-sky shading, electrical mismatch / bypass diodes and
 * weather are not modelled — the results compare layouts, they are not a yield guarantee.
 */
import type { V3 } from './structure/vec';
import { clearSkyGhi, decomposeGhi, sunPosition, type SunPosition } from './solar';

// ---------------------------------------------------------------------------------------------- location

export type ParsedLocation = { ok: true; lat: number; lon: number; source: string } | { ok: false; error: string };

const valid = (lat: number, lon: number) =>
  Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;

/**
 * Coordinates from "lat, lon" text or a Google Maps link (place pin `!3d…!4d…`, `@lat,lon`, `q=` / `query=` / `ll=` /
 * `destination=` / `center=`). Shortened links (maps.app.goo.gl, goo.gl/maps) cannot be resolved offline.
 */
export function parseLocation(input: string): ParsedLocation {
  const t = input.trim();
  if (!t) return { ok: false, error: 'Nhập toạ độ “vĩ độ, kinh độ” hoặc dán link Google Maps.' };
  const num = '(-?\\d{1,3}(?:\\.\\d+)?)';
  const plain = new RegExp(`^${num}\\s*[,;\\s]\\s*${num}$`).exec(t);
  if (plain) {
    const lat = Number(plain[1]);
    const lon = Number(plain[2]);
    return valid(lat, lon) ? { ok: true, lat, lon, source: 'toạ độ' } : { ok: false, error: 'Toạ độ ngoài phạm vi (vĩ độ ±90°, kinh độ ±180°).' };
  }
  if (/^(https?:\/\/)?(maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(t))
    return {
      ok: false,
      error: 'Link rút gọn không đọc được trực tiếp — mở link trong trình duyệt rồi sao chép link đầy đủ trên thanh địa chỉ (có “@vĩ độ,kinh độ”), hoặc nhập toạ độ.',
    };
  let s = t;
  try {
    s = decodeURIComponent(t);
  } catch {
    /* giữ nguyên */
  }
  const tries: [RegExp, string][] = [
    [new RegExp(`!3d${num}!4d${num}`), 'điểm ghim Google Maps'],
    [new RegExp(`[?&](?:q|query|ll|destination|center|daddr)=${num},\\s*${num}`), 'tham số link Google Maps'],
    [new RegExp(`@${num},${num}`), 'tâm bản đồ Google Maps'],
  ];
  for (const [re, source] of tries) {
    const m = re.exec(s);
    if (m) {
      const lat = Number(m[1]);
      const lon = Number(m[2]);
      if (valid(lat, lon)) return { ok: true, lat, lon, source };
    }
  }
  return { ok: false, error: 'Không tìm thấy toạ độ trong link — dùng link đầy đủ của Google Maps hoặc nhập “vĩ độ, kinh độ”.' };
}

/** Day of year (1–366) of a calendar date (local). */
export function dayOfYear(year: number, month: number, day: number): number {
  return Math.round((Date.UTC(year, month - 1, day) - Date.UTC(year, 0, 1)) / 86_400_000) + 1;
}

/** Unit vector towards the sun in the design world (Y up). */
export function sunVector(sun: Pick<SunPosition, 'zenithDeg' | 'azimuthDeg'>, upBearingDeg: number): V3 {
  const el = ((90 - sun.zenithDeg) * Math.PI) / 180;
  const t = ((sun.azimuthDeg - upBearingDeg) * Math.PI) / 180;
  const h = Math.cos(el);
  return [Math.sin(t) * h, Math.sin(el), -Math.cos(t) * h];
}

// ---------------------------------------------------------------------------------------------- scene

/** What casts a shadow — losses are reported per category. */
export const SHADE_CATEGORIES = [
  'BUILDING',
  'STRUCTURE',
  'OBSTACLE',
  'EQUIPMENT',
  'PANEL',
  'NEIGHBOR',
  'TREE',
  'BESS',
  'OTHER',
] as const;
export type ShadeCategory = (typeof SHADE_CATEGORIES)[number];
export const SHADE_CATEGORY_LABEL: Record<ShadeCategory, string> = {
  BUILDING: 'Nhà & mái trong dự án (tường, mái, tường chắn mái)',
  STRUCTURE: 'Khung giàn / kết cấu (kể cả nhà để xe)',
  OBSTACLE: 'Vật cản trên mái',
  EQUIPMENT: 'Thiết bị điện',
  PANEL: 'Tấm pin khác (hàng trước / mái khác)',
  NEIGHBOR: 'Nhà / công trình lân cận',
  TREE: 'Cây xanh',
  BESS: 'Thiết bị BESS',
  OTHER: 'Khác',
};
const CAT_INDEX = new Map(SHADE_CATEGORIES.map((c, i) => [c, i]));

/**
 * Triangles (9 numbers each) with an owner tag (−1 = none; a module never shades itself — same tag) and the category
 * of the object they belong to.
 */
export interface ShadeScene {
  tris: number[];
  tags: number[];
  cats: number[];
}

export function emptyScene(): ShadeScene {
  return { tris: [], tags: [], cats: [] };
}

export function addTri(s: ShadeScene, a: V3, b: V3, c: V3, tag = -1, cat: ShadeCategory = 'OTHER') {
  s.tris.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
  s.tags.push(tag);
  s.cats.push(CAT_INDEX.get(cat)!);
}

/** Planar polygon (any shape, consistent winding) → triangles by ear clipping on its dominant plane. */
export function addPolygon(s: ShadeScene, pts: readonly V3[], tag = -1, cat: ShadeCategory = 'OTHER') {
  if (pts.length < 3) return;
  if (pts.length === 3) return addTri(s, pts[0]!, pts[1]!, pts[2]!, tag, cat);
  let nx = 0,
    ny = 0,
    nz = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i]!;
    const b = pts[(i + 1) % pts.length]!;
    nx += (a[1] - b[1]) * (a[2] + b[2]);
    ny += (a[2] - b[2]) * (a[0] + b[0]);
    nz += (a[0] - b[0]) * (a[1] + b[1]);
  }
  const ax = Math.abs(nx) >= Math.abs(ny) && Math.abs(nx) >= Math.abs(nz) ? 0 : Math.abs(ny) >= Math.abs(nz) ? 1 : 2;
  const [u, v] = ax === 0 ? [1, 2] : ax === 1 ? [2, 0] : [0, 1];
  const p2 = pts.map((q) => [q[u]!, q[v]!] as [number, number]);
  let area = 0;
  for (let i = 0; i < p2.length; i++) {
    const a = p2[i]!;
    const b = p2[(i + 1) % p2.length]!;
    area += a[0] * b[1] - b[0] * a[1];
  }
  const sign = area >= 0 ? 1 : -1;
  const idx = pts.map((_, i) => i);
  const cross = (o: number, a: number, b: number) =>
    (p2[a]![0] - p2[o]![0]) * (p2[b]![1] - p2[o]![1]) - (p2[a]![1] - p2[o]![1]) * (p2[b]![0] - p2[o]![0]);
  const inside = (p: number, a: number, b: number, c: number) =>
    cross(a, b, p) * sign >= 0 && cross(b, c, p) * sign >= 0 && cross(c, a, p) * sign >= 0;
  let guard = 0;
  while (idx.length > 3 && guard++ < 10_000) {
    let cut = false;
    for (let i = 0; i < idx.length; i++) {
      const a = idx[(i + idx.length - 1) % idx.length]!;
      const b = idx[i]!;
      const c = idx[(i + 1) % idx.length]!;
      if (cross(a, b, c) * sign <= 1e-12) continue;
      if (idx.some((p) => p !== a && p !== b && p !== c && inside(p, a, b, c))) continue;
      addTri(s, pts[a]!, pts[b]!, pts[c]!, tag, cat);
      idx.splice(i, 1);
      cut = true;
      break;
    }
    if (!cut) break;
  }
  if (idx.length === 3) addTri(s, pts[idx[0]!]!, pts[idx[1]!]!, pts[idx[2]!]!, tag, cat);
}

/** Closed prism: base polygon raised to `topY` along +Y (top + sides). */
export function addPrism(s: ShadeScene, base: readonly V3[], topY: number, tag = -1, cat: ShadeCategory = 'OTHER') {
  const top = base.map((q) => [q[0], topY, q[2]] as V3);
  addPolygon(s, top, tag, cat);
  addPolygon(s, base, tag, cat);
  for (let i = 0; i < base.length; i++) {
    const j = (i + 1) % base.length;
    addTri(s, base[i]!, base[j]!, top[j]!, tag, cat);
    addTri(s, base[i]!, top[j]!, top[i]!, tag, cat);
  }
}

/** Box around a segment (member) with a b × h cross-section. */
export function addBeam(s: ShadeScene, a: V3, b: V3, width: number, depth: number, tag = -1, cat: ShadeCategory = 'STRUCTURE') {
  const d: V3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const L = Math.hypot(...d);
  if (!(L > 1e-6)) return;
  const ax: V3 = [d[0] / L, d[1] / L, d[2] / L];
  const ref: V3 = Math.abs(ax[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
  let p: V3 = [ax[1] * ref[2] - ax[2] * ref[1], ax[2] * ref[0] - ax[0] * ref[2], ax[0] * ref[1] - ax[1] * ref[0]];
  const pl = Math.hypot(...p);
  p = [p[0] / pl, p[1] / pl, p[2] / pl];
  const q: V3 = [ax[1] * p[2] - ax[2] * p[1], ax[2] * p[0] - ax[0] * p[2], ax[0] * p[1] - ax[1] * p[0]];
  const w = width / 2;
  const h = depth / 2;
  const corner = (o: V3, sw: number, sh: number): V3 => [o[0] + p[0] * sw * w + q[0] * sh * h, o[1] + p[1] * sw * w + q[1] * sh * h, o[2] + p[2] * sw * w + q[2] * sh * h];
  const ring = (o: V3) => [corner(o, -1, -1), corner(o, 1, -1), corner(o, 1, 1), corner(o, -1, 1)];
  const r0 = ring(a);
  const r1 = ring(b);
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    addTri(s, r0[i]!, r0[j]!, r1[j]!, tag, cat);
    addTri(s, r0[i]!, r1[j]!, r1[i]!, tag, cat);
  }
  addPolygon(s, r0, tag, cat);
  addPolygon(s, r1, tag, cat);
}

// ---------------------------------------------------------------------------------------------- ray casting

/** Ray–shadow tester for one sun direction (uniform grid in the plane perpendicular to the direction). */
export class ShadowCaster {
  private e1: V3;
  private e2: V3;
  private cell: number;
  private grid = new Map<number, number[]>();
  /** Projected bounding box of each triangle (u0, u1, v0, v1) */
  private box: Float64Array;
  /** Last query that saw each triangle (de-duplication without a Set) */
  private seen: Int32Array;
  private query = 0;
  constructor(
    private scene: ShadeScene,
    private dir: V3,
    cellM = 1,
  ) {
    const ref: V3 = Math.abs(dir[1]) < 0.9 ? [0, 1, 0] : [1, 0, 0];
    let e1: V3 = [dir[1] * ref[2] - dir[2] * ref[1], dir[2] * ref[0] - dir[0] * ref[2], dir[0] * ref[1] - dir[1] * ref[0]];
    const l = Math.hypot(...e1);
    e1 = [e1[0] / l, e1[1] / l, e1[2] / l];
    this.e1 = e1;
    this.e2 = [dir[1] * e1[2] - dir[2] * e1[1], dir[2] * e1[0] - dir[0] * e1[2], dir[0] * e1[1] - dir[1] * e1[0]];
    this.cell = cellM;
    const T = scene.tris;
    this.box = new Float64Array(scene.tags.length * 4);
    this.seen = new Int32Array(scene.tags.length);
    for (let k = 0; k < scene.tags.length; k++) {
      let u0 = Infinity,
        u1 = -Infinity,
        v0 = Infinity,
        v1 = -Infinity;
      for (let c = 0; c < 3; c++) {
        const o = k * 9 + c * 3;
        const u = T[o]! * e1[0] + T[o + 1]! * e1[1] + T[o + 2]! * e1[2];
        const v = T[o]! * this.e2[0] + T[o + 1]! * this.e2[1] + T[o + 2]! * this.e2[2];
        u0 = Math.min(u0, u);
        u1 = Math.max(u1, u);
        v0 = Math.min(v0, v);
        v1 = Math.max(v1, v);
      }
      this.box.set([u0, u1, v0, v1], k * 4);
      for (let i = Math.floor(u0 / this.cell); i <= Math.floor(u1 / this.cell); i++)
        for (let j = Math.floor(v0 / this.cell); j <= Math.floor(v1 / this.cell); j++) {
          const key = i * 1_000_003 + j;
          const list = this.grid.get(key);
          if (list) list.push(k);
          else this.grid.set(key, [k]);
        }
    }
  }

  /** Triangles whose projection (along the sun direction) may overlap the projection of `pts`. */
  candidates(pts: readonly V3[]): number[] {
    let u0 = Infinity,
      u1 = -Infinity,
      v0 = Infinity,
      v1 = -Infinity;
    for (const p of pts) {
      const u = p[0] * this.e1[0] + p[1] * this.e1[1] + p[2] * this.e1[2];
      const v = p[0] * this.e2[0] + p[1] * this.e2[1] + p[2] * this.e2[2];
      u0 = Math.min(u0, u);
      u1 = Math.max(u1, u);
      v0 = Math.min(v0, v);
      v1 = Math.max(v1, v);
    }
    const q = ++this.query;
    const B = this.box;
    const out: number[] = [];
    for (let i = Math.floor(u0 / this.cell); i <= Math.floor(u1 / this.cell); i++)
      for (let j = Math.floor(v0 / this.cell); j <= Math.floor(v1 / this.cell); j++)
        for (const k of this.grid.get(i * 1_000_003 + j) ?? []) {
          if (this.seen[k] === q) continue;
          this.seen[k] = q;
          const o = k * 4;
          if (B[o]! <= u1 && B[o + 1]! >= u0 && B[o + 2]! <= v1 && B[o + 3]! >= v0) out.push(k);
        }
    return out;
  }

  /** True when the ray from `p` towards the sun hits a triangle not tagged `skipTag`. */
  blocked(p: V3, skipTag: number): boolean {
    return this.hit(p, skipTag, true) >= 0;
  }

  /**
   * Category index of the NEAREST triangle hit by the ray from `p` towards the sun (−1 = none). `any` stops at the
   * first hit found (faster, category not meaningful).
   */
  hit(p: V3, skipTag: number, any = false): number {
    const u = p[0] * this.e1[0] + p[1] * this.e1[1] + p[2] * this.e1[2];
    const v = p[0] * this.e2[0] + p[1] * this.e2[1] + p[2] * this.e2[2];
    const list = this.grid.get(Math.floor(u / this.cell) * 1_000_003 + Math.floor(v / this.cell));
    if (!list) return -1;
    let best = Infinity;
    let cat = -1;
    const T = this.scene.tris;
    const d = this.dir;
    for (const k of list) {
      if (skipTag >= 0 && this.scene.tags[k] === skipTag) continue;
      const o = k * 9;
      // Möller–Trumbore
      const e1x = T[o + 3]! - T[o]!,
        e1y = T[o + 4]! - T[o + 1]!,
        e1z = T[o + 5]! - T[o + 2]!;
      const e2x = T[o + 6]! - T[o]!,
        e2y = T[o + 7]! - T[o + 1]!,
        e2z = T[o + 8]! - T[o + 2]!;
      const px = d[1] * e2z - d[2] * e2y,
        py = d[2] * e2x - d[0] * e2z,
        pz = d[0] * e2y - d[1] * e2x;
      const det = e1x * px + e1y * py + e1z * pz;
      if (Math.abs(det) < 1e-12) continue;
      const inv = 1 / det;
      const tx = p[0] - T[o]!,
        ty = p[1] - T[o + 1]!,
        tz = p[2] - T[o + 2]!;
      const a = (tx * px + ty * py + tz * pz) * inv;
      if (a < 0 || a > 1) continue;
      const qx = ty * e1z - tz * e1y,
        qy = tz * e1x - tx * e1z,
        qz = tx * e1y - ty * e1x;
      const b = (d[0] * qx + d[1] * qy + d[2] * qz) * inv;
      if (b < 0 || a + b > 1) continue;
      const t = (e2x * qx + e2y * qy + e2z * qz) * inv;
      if (t > 1e-4 && t < best) {
        best = t;
        cat = this.scene.cats[k] ?? SHADE_CATEGORIES.length - 1;
        if (any) return cat;
      }
    }
    return cat;
  }
}

/** A module to evaluate: top-face corners (TL, TR, BR, BL), outward normal, owner tag of its own triangles. */
export interface ShadeTarget {
  corners: V3[];
  normal: V3;
  tag: number;
}

/**
 * Shaded fraction (0–1) of each module for one sun direction. Modules facing away from the sun (sun behind the module
 * plane) get 1 — they receive no direct beam.
 */
export function shadedFractions(scene: ShadeScene, targets: readonly ShadeTarget[], dir: V3, samples = 3): number[] {
  return shadeDetail(scene, targets, dir, samples).fractions;
}

/** Height of a point above the module plane below which an object counts as lying in / behind the plane (m). */
const PLANE_EPS = 1e-3;

/**
 * Shaded fraction per module and, per module, the fraction shaded by each category (nearest occluder). `samples` sets
 * the resolution: the module is measured on `max(16, 8 · samples)` strips.
 */
export function shadeDetail(
  scene: ShadeScene,
  targets: readonly ShadeTarget[],
  dir: V3,
  samples = 3,
): { fractions: number[]; byCategory: number[][] } {
  // Ô lưới 2 m: mỗi tấm chỉ chạm vài ô, danh sách ứng viên đã được lọc theo khung bao hình chiếu
  const caster = new ShadowCaster(scene, dir, 2);
  const strips = Math.max(16, Math.round(samples) * 8);
  const byCategory: number[][] = [];
  const fractions = targets.map((t) => {
    const cats = new Array<number>(SHADE_CATEGORIES.length).fill(0);
    byCategory.push(cats);
    return moduleShade(scene, caster, t, dir, strips, cats);
  });
  return { fractions, byCategory };
}

interface ShadowPoly {
  /** Shadow outline in module coordinates (u along TL→TR, v along TL→BL, both 0–1) */
  pts: [number, number][];
  /** Occluding triangle (index into the scene) — for the distance to the nearest occluder */
  k: number;
  cat: number;
}

/** Shaded fraction of one module; adds the share of each category (nearest occluder) to `cats`. */
function moduleShade(scene: ShadeScene, caster: ShadowCaster, t: ShadeTarget, dir: V3, strips: number, cats: number[]): number {
  const n = t.normal;
  const dn = n[0] * dir[0] + n[1] * dir[1] + n[2] * dir[2];
  if (dn <= 0) return 1;
  const [a, b, , d] = t.corners as [V3, V3, V3, V3];
  const eu: V3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const ev: V3 = [d[0] - a[0], d[1] - a[1], d[2] - a[2]];
  const guu = eu[0] * eu[0] + eu[1] * eu[1] + eu[2] * eu[2];
  const gvv = ev[0] * ev[0] + ev[1] * ev[1] + ev[2] * ev[2];
  const guv = eu[0] * ev[0] + eu[1] * ev[1] + eu[2] * ev[2];
  const det = guu * gvv - guv * guv;
  if (!(det > 1e-12)) return 0;
  /** Module coordinates of the shadow of point `q` (height `h` above the module plane) cast along the sun direction */
  const uvOf = (q: V3, h: number): [number, number] => {
    const s = h / dn;
    const rx = q[0] - dir[0] * s - a[0];
    const ry = q[1] - dir[1] * s - a[1];
    const rz = q[2] - dir[2] * s - a[2];
    const ru = rx * eu[0] + ry * eu[1] + rz * eu[2];
    const rv = rx * ev[0] + ry * ev[1] + rz * ev[2];
    return [(gvv * ru - guv * rv) / det, (guu * rv - guv * ru) / det];
  };
  const T = scene.tris;
  const polys: ShadowPoly[] = [];
  for (const k of caster.candidates(t.corners)) {
    if (scene.tags[k] === t.tag) continue;
    const o = k * 9;
    const V: V3[] = [
      [T[o]!, T[o + 1]!, T[o + 2]!],
      [T[o + 3]!, T[o + 4]!, T[o + 5]!],
      [T[o + 6]!, T[o + 7]!, T[o + 8]!],
    ];
    const H = V.map((q) => (q[0] - a[0]) * n[0] + (q[1] - a[1]) * n[1] + (q[2] - a[2]) * n[2]);
    if (H[0]! <= PLANE_EPS && H[1]! <= PLANE_EPS && H[2]! <= PLANE_EPS) continue;
    // Part of the triangle in front of the module plane (towards the sun), then its shadow on the module
    const pts: [number, number][] = [];
    for (let i = 0; i < 3; i++) {
      const p = V[i]!;
      const q = V[(i + 1) % 3]!;
      const hp = H[i]! - PLANE_EPS;
      const hq = H[(i + 1) % 3]! - PLANE_EPS;
      if (hp > 0) pts.push(uvOf(p, H[i]!));
      if (hp > 0 !== hq > 0) {
        const f = hp / (hp - hq);
        pts.push(uvOf([p[0] + (q[0] - p[0]) * f, p[1] + (q[1] - p[1]) * f, p[2] + (q[2] - p[2]) * f], PLANE_EPS));
      }
    }
    if (pts.length < 3) continue;
    if (pts.every((q) => q[0] <= 0) || pts.every((q) => q[0] >= 1) || pts.every((q) => q[1] <= 0) || pts.every((q) => q[1] >= 1)) continue;
    polys.push({ pts, k, cat: scene.cats[k] ?? SHADE_CATEGORIES.length - 1 });
  }
  if (!polys.length) return 0;
  /** Distance along the ray from the module point (u, v) to the plane of triangle k (nearest occluder) */
  const depth = (k: number, u: number, v: number) => {
    const o = k * 9;
    const e1: V3 = [T[o + 3]! - T[o]!, T[o + 4]! - T[o + 1]!, T[o + 5]! - T[o + 2]!];
    const e2: V3 = [T[o + 6]! - T[o]!, T[o + 7]! - T[o + 1]!, T[o + 8]! - T[o + 2]!];
    const nt: V3 = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
    const den = nt[0] * dir[0] + nt[1] * dir[1] + nt[2] * dir[2];
    if (Math.abs(den) < 1e-12) return Infinity;
    const p: V3 = [a[0] + eu[0] * u + ev[0] * v, a[1] + eu[1] * u + ev[1] * v, a[2] + eu[2] * u + ev[2] * v];
    return ((T[o]! - p[0]) * nt[0] + (T[o + 1]! - p[1]) * nt[1] + (T[o + 2]! - p[2]) * nt[2]) / den;
  };
  // Strips at constant u: on each, the shadow of a convex polygon is one interval of v (exact)
  let shaded = 0;
  for (let i = 0; i < strips; i++) {
    const u = (i + 0.5) / strips;
    const spans: { v0: number; v1: number; p: ShadowPoly }[] = [];
    for (const p of polys) {
      let v0 = Infinity,
        v1 = -Infinity;
      for (let j = 0; j < p.pts.length; j++) {
        const [pu, pv] = p.pts[j]!;
        const [qu, qv] = p.pts[(j + 1) % p.pts.length]!;
        if (pu === qu) {
          if (Math.abs(pu - u) < 1e-12) {
            v0 = Math.min(v0, pv, qv);
            v1 = Math.max(v1, pv, qv);
          }
          continue;
        }
        if ((pu - u) * (qu - u) > 0) continue;
        const v = pv + ((qv - pv) * (u - pu)) / (qu - pu);
        v0 = Math.min(v0, v);
        v1 = Math.max(v1, v);
      }
      v0 = Math.max(0, v0);
      v1 = Math.min(1, v1);
      if (v1 > v0) spans.push({ v0, v1, p });
    }
    if (!spans.length) continue;
    // Union of the intervals; each elementary piece goes to the nearest occluder covering it
    const cuts = [...new Set(spans.flatMap((x) => [x.v0, x.v1]))].sort((x, y) => x - y);
    for (let j = 0; j + 1 < cuts.length; j++) {
      const lo = cuts[j]!;
      const hi = cuts[j + 1]!;
      const mid = (lo + hi) / 2;
      let best: ShadowPoly | null = null;
      let bestT = Infinity;
      for (const x of spans) {
        if (x.v0 > mid || x.v1 < mid) continue;
        if (!best && spans.length === 1) {
          best = x.p;
          break;
        }
        const tt = depth(x.p.k, u, mid);
        if (!best || tt < bestT) {
          best = x.p;
          bestT = tt;
        }
      }
      if (!best) continue;
      const len = (hi - lo) / strips;
      shaded += len;
      cats[best.cat]! += len;
    }
  }
  // Cộng dồn số thực: che kín / không che phải ra đúng 1 / 0
  return shaded > 1 - 1e-9 ? 1 : shaded < 1e-12 ? 0 : shaded;
}

// ---------------------------------------------------------------------------------------------- day / year

export interface SiteTime {
  lat: number;
  lon: number;
  /** Time zone (hours from UTC), e.g. 7 for Vietnam */
  utcOffset: number;
  upBearingDeg: number;
}

export interface ShadeStep {
  hour: number;
  sun: SunPosition;
  /** Sun above the horizon (elevation > 2°) */
  up: boolean;
  fractions: number[];
}

export interface ShadeAnalysis {
  steps: ShadeStep[];
  /** Per module: clear-sky direct-beam irradiation without / with shadows (Wh/m²) and the loss share */
  perTarget: { beamWh: number; shadedBeamWh: number; lossPct: number; shadedHours: number }[];
  /** Whole array (sum over modules) */
  lossPct: number;
  beamWh: number;
  shadedBeamWh: number;
  /** Share of the array's beam irradiation lost to each category of occluder (%) */
  lossByCategoryPct: Record<ShadeCategory, number>;
}

/** One day at `stepMin` resolution; `days` > 1 sums several days (e.g. a representative day per month). */
export function analyseShading(
  scene: ShadeScene,
  targets: readonly ShadeTarget[],
  site: SiteTime,
  days: readonly number[],
  stepMin = 15,
  samples = 3,
): ShadeAnalysis {
  const per = targets.map(() => ({ beamWh: 0, shadedBeamWh: 0, lossPct: 0, shadedHours: 0 }));
  const catWh = new Array<number>(SHADE_CATEGORIES.length).fill(0);
  const steps: ShadeStep[] = [];
  const dt = stepMin / 60;
  for (const doy of days)
    for (let h = dt / 2; h < 24; h += dt) {
      const sun = sunPosition(site.lat, site.lon, site.utcOffset, doy, h);
      const up = 90 - sun.zenithDeg > 2;
      if (!up) {
        if (days.length === 1) steps.push({ hour: h, sun, up, fractions: [] });
        continue;
      }
      const dir = sunVector(sun, site.upBearingDeg);
      const det = shadeDetail(scene, targets, dir, samples);
      const f = det.fractions;
      if (days.length === 1) steps.push({ hour: h, sun, up, fractions: f });
      const dni = decomposeGhi(clearSkyGhi(sun.cosZenith), sun.cosZenith, doy).dni;
      targets.forEach((t, i) => {
        const cos = Math.max(0, t.normal[0] * dir[0] + t.normal[1] * dir[1] + t.normal[2] * dir[2]);
        const beam = dni * cos * dt;
        per[i]!.beamWh += beam;
        per[i]!.shadedBeamWh += beam * f[i]!;
        if (cos > 0) det.byCategory[i]!.forEach((x, c) => (catWh[c]! += beam * x));
        if (cos > 0 && f[i]! >= 0.5) per[i]!.shadedHours += dt / days.length;
      });
    }
  for (const p of per) p.lossPct = p.beamWh > 0 ? (p.shadedBeamWh / p.beamWh) * 100 : 0;
  const beamWh = per.reduce((t, p) => t + p.beamWh, 0);
  const shadedBeamWh = per.reduce((t, p) => t + p.shadedBeamWh, 0);
  const lossByCategoryPct = Object.fromEntries(
    SHADE_CATEGORIES.map((c, i) => [c, beamWh > 0 ? (catWh[i]! / beamWh) * 100 : 0]),
  ) as Record<ShadeCategory, number>;
  return { steps, perTarget: per, lossPct: beamWh > 0 ? (shadedBeamWh / beamWh) * 100 : 0, beamWh, shadedBeamWh, lossByCategoryPct };
}

/** Day of year of the 21st of each month (representative days for a yearly estimate). */
export const REPRESENTATIVE_DAYS = [21, 52, 80, 111, 141, 172, 202, 233, 264, 294, 325, 355];
